import { DateFormatter } from '@components'

import { amountToDisplayValue, PaymentPane, PaymentPaneProps, Text } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { InvoiceBalanceModifier } from '@features/invoices/types/invoiceBalanceModifier'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useEmberRouteToUrlGenerator } from '../../../../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { getOrganizationViewUrl } from '../../../../../../../../utils/routing/getOrganizationViewUrl'
import { isEmberRoute } from '../../../../../../../../utils/routing/isEmberRoute'
import { externalInvoiceBalanceModifierReferenceToIconName } from '../../../../../../../invoices/elements/InvoicePaymentPane/constants/invoiceBalanceModifierReferenceToIconName'
import { getExternalInvoiceBalanceModifierReference } from '../../../../utils/getExternalInvoiceBalanceModifierReference'
import { getExternalInvoicePaymentPaneItemRoute } from '../../../../utils/getExternalInvoicePaymentPaneItemRoute'
import { ExternalInvoiceBalanceModifierReference } from '../../../InvoiceViewActions/enums/ExternalInvoiceBalanceModifierReference'
import { externalCreditnoteBalanceModifierReferenceToTKey } from '../../constants/externalCreditnoteBalanceModifierReferenceToKey'
import { externalInvoiceBalanceModifierReferenceToTKey } from '../../constants/externalInvoiceBalanceModifierRefereceToKey'

type ExternalInvoicePaymentPaneItemProps = InvoiceBalanceModifier &
  Pick<PaymentPaneProps, 'withHoverEffect'> & {
    invoiceType: InvoiceType | null
    isCardPayment?: boolean
  }

export const ExternalInvoicePaymentPaneItem = ({
  amount,
  entryDate,
  invoiceType,
  isCardPayment = false,
  modifierReference,
  withHoverEffect,
}: ExternalInvoicePaymentPaneItemProps): ReactElement => {
  const { t } = useTranslation()
  const { generateUrl } = useEmberRouteToUrlGenerator()
  const navigate = useNavigate()
  const location = useLocation()
  const { organization } = useUserOrganization()

  const modifierData = useMemo(() => getExternalInvoiceBalanceModifierReference(modifierReference), [modifierReference])

  const { modifierType, modifierId } = modifierData || {}
  const contentText = useMemo(() => {
    if (modifierType) {
      if (isCardPayment) {
        return t('invoices.view.payment_pane.modifier.card_payment')
      }
      if (invoiceType === InvoiceType.CreditNote) {
        return t(externalCreditnoteBalanceModifierReferenceToTKey[modifierType])
      }
      return t(externalInvoiceBalanceModifierReferenceToTKey[modifierType])
    }
  }, [invoiceType, isCardPayment, modifierType, t])

  const icon = useMemo(() => {
    if (isCardPayment) {
      return 'creditCard'
    }
    if (modifierType) {
      return externalInvoiceBalanceModifierReferenceToIconName[modifierType]
    }
  }, [isCardPayment, modifierType])

  const handleClick = useCallback(() => {
    if (modifierType && modifierId) {
      const route = getExternalInvoicePaymentPaneItemRoute(modifierId, modifierType)

      if (isEmberRoute(route)) {
        if (generateUrl) {
          const url = generateUrl(route, modifierId)
          navigate(url)
        }
      } else {
        navigate(`${getOrganizationViewUrl(organization?.url, route)}${location.search}`)
      }
    }
  }, [modifierType, modifierId, generateUrl, navigate, organization?.url, location.search])

  const isFee = modifierType === ExternalInvoiceBalanceModifierReference.InvoiceLateFee

  return (
    <PaymentPane.Item
      amount={amount !== null && <Text>{amountToDisplayValue(amount)}</Text>}
      content={
        <Text variant="bodySM" colorVariant="secondary">
          {contentText}
        </Text>
      }
      extra={
        <Text colorVariant="secondary" variant="bodySM">
          {entryDate && <DateFormatter value={entryDate} />}
        </Text>
      }
      icon={icon}
      onClick={handleClick}
      prefix={
        <Text colorVariant={isFee ? 'danger' : 'success'} variant="bodySM">
          {isFee
            ? t('invoices.view.payment_pane.item.prefix_fee')
            : t('invoices.view.payment_pane.item.prefix_payment')}
        </Text>
      }
      withHoverEffect={withHoverEffect}
    />
  )
}
